/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @next/next/no-img-element */
import {
  ChangeEventHandler,
  FC,
  memo,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import { RiArrowDropRightLine } from 'react-icons/ri';
import { IoMdClose } from 'react-icons/io';
import { createPortal } from 'react-dom';

import { useEventListener, useDebounceValue } from 'usehooks-ts';
import { CSSProperties, useTheme } from 'styled-components';

import { inter } from '~/common/styles/fonts';

import {
  IDropdownOption,
  IDropdownProps,
  TDropdownPosition,
  TDropdownValue,
  TOnChangeDropdown,
} from './types';
import {
  Container,
  InputWrapper,
  CustomInput,
  RightIconWrapper,
  StyIconLabelContent,
  CloseIcon,
} from './styles';
import { DropdownOptions } from './DropdownOptions';
import { Typography } from '../Typograph';
import { HelperText } from '../HelperText';
import { FormLabel } from '../FormLabel';

const BaseDropdown: FC<IDropdownProps> = ({
  onChange = () => null,
  onDropdownOpen = () => null,
  onDropdownClose = () => null,
  withoutClear = false,
  returnFullAsyncValue = false,
  withoutValue = false,
  hideOptionLabel = false,
  filter = [],
  value: _value,
  options = [],
  placeholder = 'Selecione uma opção',
  label,
  size = 'medium',
  error,
  defaultValue,
  asyncParams = [],
  noOptionsText,
  scrollViewId = 'main-element',
  containerStyle = {},
  name,
  isDisabled,
  helperText,
  leftIcon: LeftIcon,
  leftCustomIcon,
  width = '300px',
  variant = 'default',
  borderRadius = 'default',
  valueType = 'string',
  asyncData,
  backgroundColor = 'neutral950',
  onHoverBackgroundColor = 'neutral900',
  borderColor = 'neutral700',
  onFocusBorderColor = 'neutral0',
  currentOptionTextColor = 'neutral600',
  disabledOptionTextColor = 'neutral500',
  placeholderTextColor = 'neutral500',
  dropdownBorderColor,
  dropdownOptionTextColor,
  dropdownBackgroundColor,
  optionHoverBackgroundColor,
  dropdownSelectedOptionBackgroundColor,
}) => {
  const id = useId();
  const theme = useTheme();
  const [mounted, setMounted] = useState(false);
  const [portalContainer, setPortalContainer] = useState<Element | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const inputWrapperRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLLabelElement>(null);
  const memorizedFirstAsyncState = useRef<IDropdownOption[]>(null);

  const [asyncOptions, setAsyncOptions] = useState<IDropdownOption[]>([]);
  const [debouncedValue, setSearchValue] = useDebounceValue('', 250);
  const [containerClientRect, setContainerClientRect] = useState<DOMRect>();
  const [inputWrapperClientRect, setInputWrapperClientRect] =
    useState<DOMRect>();
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [value, setValue] = useState<TDropdownValue>('');
  const [position, setPosition] = useState<TDropdownPosition>('bottom');
  const [asyncValueOption, setAsyncValueOption] = useState<IDropdownOption>();

  const handleSetClientRect = (): void => {
    const _containerClientRect = containerRef.current?.getBoundingClientRect();
    const _inputWrapperClientRect =
      inputWrapperRef.current?.getBoundingClientRect();
    if (!_containerClientRect || !_inputWrapperClientRect) return;

    const isAtScreen =
      _inputWrapperClientRect.bottom + 216 <= window.innerHeight;

    setContainerClientRect(_containerClientRect);
    setInputWrapperClientRect(_inputWrapperClientRect);
    setPosition(isAtScreen ? 'bottom' : 'top');
  };

  const handleSelectChange: TOnChangeDropdown = useCallback(
    (option) => {
      if (asyncData) {
        const found = asyncOptions.find((where) => where.value === option);
        setAsyncValueOption(found);

        if (returnFullAsyncValue) {
          onChange(found);
          return;
        }
      }

      onChange(option, name);
    },
    [asyncData, asyncOptions, returnFullAsyncValue, name, onChange]
  );

  // const asyncLoad = useCallback(async () => {
  /** @TODO - implementar caso necessário ou apagar se não formos utilizar */
  // setAsyncOptions([]);
  // try {
  //   const method = asyncData.method || 'post';
  //   const payload = {
  //     [asyncData.key]: debouncedValue,
  //     ...(asyncData.defaultParams || {}),
  //   };
  //   let response = null;
  //   if (method === 'post') {
  //     const { data } = await customAxios.post(asyncData.route, payload);
  //     response = data;
  //   } else {
  //     const { data } = await customAxios.get(asyncData.route, {
  //       params: payload,
  //     });
  //     response = data;
  //   }
  //   const toMap = asyncData.arrayResponseField
  //     ? response[asyncData.arrayResponseField]
  //     : response;
  //   const mapped = toMap.map(asyncData.mapper) as IDropdownOption[];
  //   if (!mapped.length) {
  //     return;
  //   }
  //   if (!memorizedFirstAsyncState.current) {
  //     memorizedFirstAsyncState.current = mapped;
  //   }
  //   if (!mapped || !mapped.length) {
  //     setAsyncOptions(memorizedFirstAsyncState.current || []);
  //   } else {
  //     setAsyncOptions((prevState) => [
  //       ...prevState,
  //       ...mapped.filter(
  //         (opt) => !prevState.find((prv) => prv.value === opt.value)
  //       ),
  //     ]);
  //   }
  // } catch {
  //   if (!userType || !vipUsers.includes(userType)) {
  //     return;
  //   }
  //   showToast({
  //     type: 'error',
  //     title: 'Erro',
  //     description:
  //       asyncData.errorMessage || 'Houve um erro ao carregar as opções',
  //   });
  // }
  // }, [asyncData, debouncedValue, ...(asyncParams || [])]);

  // useEffect(() => {
  //   if (asyncData) {
  //     asyncLoad();
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [debouncedValue, ...asyncParams]);

  useEffect(() => {
    setMounted(true);
    setPortalContainer(document.getElementById('portal-root'));
    handleSetClientRect();
  }, []);

  useEffect(() => {
    setValue(_value);
  }, [_value]);

  useEffect(() => {
    if (defaultValue) {
      setValue(defaultValue);
    } else if (asyncData) {
      setAsyncValueOption(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue]);

  const openDropdown = (): void => {
    if (isDropdownVisible || isDisabled) return;

    handleSetClientRect();
    setIsDropdownVisible(true);
    inputRef.current?.focus();
  };

  const closeDropdown = (): void => {
    if (!isDropdownVisible) return;
    setIsDropdownVisible(false);
    setSearchValue('');

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleClickSelect = (): void => {
    isDropdownVisible ? closeDropdown() : openDropdown();
  };

  const handleChangeSearch: ChangeEventHandler<HTMLInputElement> = (event) => {
    setSearchValue(event.target.value);
  };

  const finalOptions = useMemo(() => {
    const returning = asyncData
      ? asyncOptions.length
        ? asyncOptions
        : memorizedFirstAsyncState.current || []
      : options;

    if (filter.length) {
      const filtered = returning.filter(
        (option) => !filter.includes(String(option.value))
      );

      return filtered;
    }

    return returning;
  }, [asyncData, asyncOptions, filter, options]);

  const formattedOptions = useMemo(() => {
    if (debouncedValue) {
      return finalOptions.filter(
        (option) =>
          option.label
            ?.trim()
            ?.toLowerCase()
            ?.normalize('NFD')
            ?.includes(debouncedValue.trim().toLowerCase().normalize('NFD')) ||
          option.value
            ?.toString()
            ?.trim()
            ?.toLowerCase()
            ?.normalize('NFD')
            ?.includes(debouncedValue.trim().toLowerCase().normalize('NFD'))
      );
    }

    return finalOptions;
  }, [finalOptions, debouncedValue]);

  const optionValue: IDropdownOption | null = useMemo(() => {
    if (withoutValue) return null;

    if (asyncData && asyncValueOption) {
      return asyncValueOption;
    }

    if (!value) return null;

    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      return finalOptions?.find((option) => option.value === value);
    }

    return (value as IDropdownOption) || null;
  }, [asyncData, asyncValueOption, withoutValue, value, finalOptions]);

  useEffect(() => {
    if (!inputRef.current) return;
    inputRef.current.value = optionValue?.label || '';
  }, [optionValue]);

  const shouldRenderImageOrIcon = useMemo(() => {
    return (
      ['icon', 'image-text'].includes(valueType) &&
      (optionValue?.leftCustomIcon ||
        optionValue?.leftIcon ||
        optionValue?.image) &&
      !isDropdownVisible
    );
  }, [valueType, optionValue, isDropdownVisible]);

  useEventListener('resize', handleSetClientRect);
  useEventListener('scroll', closeDropdown, {
    ...(mounted &&
      isDropdownVisible &&
      portalContainer && { current: document.getElementById(scrollViewId) }),
  });

  const customStyle: CSSProperties = {};

  if (isDisabled) {
    customStyle.pointerEvents = 'none';
  }

  if (width) {
    customStyle.width = width;
  }

  useEffect(() => {
    if (isDropdownVisible) {
      onDropdownOpen();
    } else {
      onDropdownClose();
    }
  }, [isDropdownVisible]);

  return (
    <Container htmlFor={id} ref={containerRef} style={customStyle}>
      {!!label && <FormLabel {...label} isDisabled={isDisabled} />}
      <InputWrapper
        $hasError={!!error}
        $isDisabled={isDisabled}
        $size={size}
        style={containerStyle}
        $width={width}
        ref={inputWrapperRef}
        $variant={variant}
        $borderRadius={borderRadius}
        $backgroundColor={backgroundColor}
        $onHoverBackgroundColor={onHoverBackgroundColor}
        $borderColor={borderColor}
        $onFocusBorderColor={onFocusBorderColor}
        {...(!isDropdownVisible && { onClick: handleClickSelect })}
      >
        {(variant !== 'compact' ||
          shouldRenderImageOrIcon ||
          (!optionValue && !isDropdownVisible)) && (
          <>
            {shouldRenderImageOrIcon ? (
              <>
                {optionValue.image ? (
                  <>
                    <img
                      src={
                        typeof optionValue.image === 'string'
                          ? optionValue.image
                          : optionValue.image?.src
                      }
                      alt={optionValue.label}
                      style={{
                        width: '30px',
                        height: '30px',
                      }}
                    />
                    <Typography.Label
                      size="small"
                      color={
                        optionValue.isDisabled
                          ? disabledOptionTextColor
                          : currentOptionTextColor
                      }
                    >
                      {optionValue.label}
                    </Typography.Label>
                  </>
                ) : (
                  <>
                    {optionValue.leftCustomIcon ? (
                      <StyIconLabelContent>
                        <>{optionValue.leftCustomIcon}</>
                        {!hideOptionLabel && (
                          <Typography.Label
                            size="small"
                            color={
                              optionValue.isDisabled
                                ? 'neutral500'
                                : 'neutral400'
                            }
                          >
                            {optionValue.label}
                          </Typography.Label>
                        )}
                      </StyIconLabelContent>
                    ) : (
                      <StyIconLabelContent>
                        <optionValue.leftIcon
                          size={20}
                          color={
                            optionValue.isDisabled
                              ? theme[disabledOptionTextColor]
                              : theme[currentOptionTextColor]
                          }
                        />
                        {!hideOptionLabel && (
                          <Typography.Label
                            size="small"
                            color={
                              optionValue.isDisabled
                                ? disabledOptionTextColor
                                : currentOptionTextColor
                            }
                          >
                            {optionValue.label}
                          </Typography.Label>
                        )}
                      </StyIconLabelContent>
                    )}
                  </>
                )}
              </>
            ) : leftCustomIcon ? (
              <>{leftCustomIcon}</>
            ) : LeftIcon ? (
              <LeftIcon
                size={20}
                color={isDisabled ? theme.neutral600 : theme.neutral500}
              />
            ) : null}
          </>
        )}

        {!shouldRenderImageOrIcon &&
          (variant === 'compact' ? isDropdownVisible || !LeftIcon : true) && (
            <CustomInput
              disabled={isDisabled}
              $textColor={currentOptionTextColor}
              $placeholderColor={placeholderTextColor}
              name={name}
              id={id}
              ref={inputRef}
              onChange={handleChangeSearch}
              autoComplete="new-password"
              autoCorrect="off"
              type="text"
              $variant={variant}
              {...(variant !== 'compact' && { placeholder })}
              {...(!isDropdownVisible && { onClick: handleClickSelect })}
              {...(valueType === 'string' &&
                !!optionValue &&
                !isDropdownVisible && { value: optionValue?.label })}
              className={inter.className}
            />
          )}

        {!!optionValue && variant !== 'compact' && (
          <>
            {!withoutClear && (
              <CloseIcon
                id="close-icon"
                type="button"
                onClick={(ev) => {
                  ev.stopPropagation();
                  handleSelectChange(null);
                  ev.stopPropagation();
                }}
              >
                <IoMdClose size={12} color={theme.neutral50} />
              </CloseIcon>
            )}
          </>
        )}

        <RightIconWrapper $isDropdownVisible={isDropdownVisible}>
          <RiArrowDropRightLine
            size={20}
            color={isDisabled ? theme.neutral600 : theme.neutral500}
          />
        </RightIconWrapper>

        {mounted &&
          isDropdownVisible &&
          portalContainer &&
          createPortal(
            <DropdownOptions
              backgroundColor={dropdownBackgroundColor}
              borderColor={dropdownBorderColor}
              optionTextColor={dropdownOptionTextColor}
              optionHoverBackgroundColor={optionHoverBackgroundColor}
              containerClientRect={containerClientRect}
              inputWrapperClientRect={inputWrapperClientRect}
              position={position}
              isDropdownVisible={isDropdownVisible}
              size={size}
              borderRadius={borderRadius}
              leftCustomIcon={leftCustomIcon}
              options={formattedOptions}
              isDisabled={isDisabled}
              onCloseDropdown={closeDropdown}
              noOptionsText={noOptionsText}
              leftIcon={LeftIcon}
              variant={variant}
              selectedOption={optionValue}
              onSelectOption={handleSelectChange}
              selectedOptionBackgroundColor={
                dropdownSelectedOptionBackgroundColor
              }
            />,
            portalContainer
          )}
      </InputWrapper>

      {(!!helperText || !!error) && variant !== 'compact' && (
        <HelperText isDisabled={isDisabled} isError={!!error}>
          {error || helperText}
        </HelperText>
      )}
    </Container>
  );
};

export const Dropdown = memo(BaseDropdown);
